import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Eye, Truck, Printer } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import Modal from '../components/Modal';
import { moneda, cantidad, fechaCorta } from '../utils/format';
import { render, printDocument } from '../utils/print.js';
import { PLANTILLAS_DEFAULT } from '../data/plantillasDefault.js';

const hoy = () => new Date().toISOString().split('T')[0];
const emptyItem = () => ({ producto_id: '', cantidad: 1, precio_unitario: '' });

// ── Fila de ítem en el formulario ──────────────────────────────────────────
function ItemRow({ item, index, productos, onChange, onRemove }) {
  const prod = productos.find(p => p.id === parseInt(item.producto_id));
  const subtotal = (parseFloat(item.cantidad) || 0) * (parseFloat(item.precio_unitario) || 0);

  return (
    <div className="grid grid-cols-12 gap-2 items-center">
      <div className="col-span-5">
        <select
          className="input text-sm"
          value={item.producto_id}
          onChange={e => onChange(index, 'producto_id', e.target.value)}
        >
          <option value="">Seleccionar...</option>
          {productos.map(p => (
            <option key={p.id} value={p.id}>{p.nombre} ({p.unidad})</option>
          ))}
        </select>
      </div>
      <div className="col-span-2">
        <input className="input text-sm text-right" type="number" min="0.001" step="0.001"
          placeholder="Cant." value={item.cantidad}
          onChange={e => onChange(index, 'cantidad', e.target.value)} />
      </div>
      <div className="col-span-3">
        <input className="input text-sm text-right" type="number" min="0" step="0.01"
          placeholder="P. unitario" value={item.precio_unitario}
          onChange={e => onChange(index, 'precio_unitario', e.target.value)} />
      </div>
      <div className="col-span-1 text-right text-sm font-medium text-gray-600 tabular-nums">
        {subtotal > 0 ? `$${subtotal.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—'}
      </div>
      <div className="col-span-1 flex justify-end">
        <button onClick={() => onRemove(index)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-500 transition-colors">
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

// ── Modal detalle (solo lectura) ───────────────────────────────────────────
function DetalleModal({ compra, onClose }) {
  if (!compra) return null;

  const handlePrint = async () => {
    let html = PLANTILLAS_DEFAULT.compra.html;
    try {
      const res = await api.get('/plantillas/index.php?tipo=compra');
      if (res.data?.html) html = res.data.html;
    } catch { /* usa default */ }

    const data = {
      id: compra.id,
      fecha: fechaCorta(compra.fecha),
      proveedor: compra.proveedor || '—',
      usuario: compra.usuario,
      notas: compra.notas || '—',
      total_fmt: moneda(compra.total),
      items: (compra.items ?? []).map(item => ({
        ...item,
        precio_unitario: moneda(item.precio_unitario),
        subtotal: moneda(item.subtotal),
      })),
    };
    printDocument(render(html, data));
  };

  return (
    <Modal isOpen={!!compra} onClose={onClose} title={`Compra #${compra.id}`} size="lg"
      footer={
        <div className="flex justify-between w-full">
          <button className="btn-ghost gap-1.5 text-sm" onClick={handlePrint}>
            <Printer size={14} /> Imprimir
          </button>
          <button className="btn-secondary" onClick={onClose}>Cerrar</button>
        </div>
      }>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><span className="text-gray-400">Fecha:</span> <span className="font-medium">{fechaCorta(compra.fecha)}</span></div>
          <div><span className="text-gray-400">Proveedor:</span> <span className="font-medium">{compra.proveedor || '—'}</span></div>
          <div><span className="text-gray-400">Registrado por:</span> <span className="font-medium">{compra.usuario}</span></div>
          {compra.notas && <div className="col-span-2"><span className="text-gray-400">Notas:</span> {compra.notas}</div>}
        </div>
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr><th>Producto</th><th>Cantidad</th><th>P. Unitario</th><th>Subtotal</th></tr>
            </thead>
            <tbody>
              {compra.items?.map((item, i) => (
                <tr key={i}>
                  <td className="font-medium">{item.producto}</td>
                  <td>{cantidad(item.cantidad, item.unidad)}</td>
                  <td className="tabular-nums">{moneda(item.precio_unitario)}</td>
                  <td className="tabular-nums font-medium">{moneda(item.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end">
          <div className="text-right">
            <span className="text-gray-500 text-sm mr-3">Total</span>
            <span className="text-xl font-bold text-gray-900">{moneda(compra.total)}</span>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────
export default function Compras() {
  const [compras, setCompras]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [productos, setProductos]   = useState([]);
  const [proveedores, setProveedores] = useState([]);

  const [modalOpen, setModalOpen]   = useState(false);
  const [form, setForm]             = useState({ fecha: hoy(), proveedor_id: '', notas: '' });
  const [items, setItems]           = useState([emptyItem()]);
  const [saving, setSaving]         = useState(false);

  const [detalle, setDetalle]       = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchCompras = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/compras/index.php');
      setCompras(res.data);
    } catch { toast.error('Error al cargar compras'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCompras(); }, [fetchCompras]);

  const openModal = async () => {
    setForm({ fecha: hoy(), proveedor_id: '', notas: '' });
    setItems([emptyItem()]);
    setModalOpen(true);

    try {
      const [prods, provs] = await Promise.all([
        api.get('/productos/index.php'),
        api.get('/proveedores/index.php'),
      ]);
      setProductos(prods.data);
      setProveedores(provs.data);
    } catch { toast.error('Error al cargar datos'); }
  };

  // ── Items ─────────────────────────────────────────────────────────────────
  const addItem    = () => setItems(p => [...p, emptyItem()]);
  const removeItem = (i) => setItems(p => p.filter((_, idx) => idx !== i));
  const updateItem = (i, field, val) => setItems(p => {
    const copy = [...p];
    copy[i] = { ...copy[i], [field]: val };
    return copy;
  });

  const total = items.reduce((s, i) => s + (parseFloat(i.cantidad) || 0) * (parseFloat(i.precio_unitario) || 0), 0);

  // ── Guardar ───────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.fecha) { toast.error('La fecha es requerida'); return; }
    const validItems = items.filter(i => i.producto_id && parseFloat(i.cantidad) > 0);
    if (!validItems.length) { toast.error('Agregá al menos un ítem con producto y cantidad'); return; }

    setSaving(true);
    try {
      await api.post('/compras/index.php', {
        ...form,
        proveedor_id: form.proveedor_id || null,
        items: validItems.map(i => ({
          producto_id:    parseInt(i.producto_id),
          cantidad:       parseFloat(i.cantidad),
          precio_unitario: parseFloat(i.precio_unitario) || 0,
        })),
      });
      toast.success('Compra registrada');
      setModalOpen(false);
      fetchCompras();
    } catch (err) {
      toast.error(err?.message || 'Error al registrar');
    } finally {
      setSaving(false);
    }
  };

  // ── Ver detalle ───────────────────────────────────────────────────────────
  const verDetalle = async (id) => {
    setLoadingDetalle(true);
    try {
      const res = await api.get(`/compras/index.php?id=${id}`);
      setDetalle(res.data);
    } catch { toast.error('Error al cargar detalle'); }
    finally { setLoadingDetalle(false); }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      <div className="page-header">
        <h1 className="page-title">Compras</h1>
        <button className="btn-primary" onClick={openModal}>
          <Plus size={18} /> Nueva compra
        </button>
      </div>

      {/* Tabla historial */}
      <div className="card !p-0 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
          </div>
        ) : compras.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-300">
            <Truck size={48} />
            <p className="text-sm mt-3 text-gray-400">No hay compras registradas</p>
            <button className="btn-primary mt-4" onClick={openModal}><Plus size={16} /> Primera compra</button>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr><th>Fecha</th><th>Proveedor</th><th>Ítems</th><th>Total</th><th>Registró</th><th></th></tr>
              </thead>
              <tbody>
                {compras.map(c => (
                  <tr key={c.id}>
                    <td className="tabular-nums text-gray-600">{fechaCorta(c.fecha)}</td>
                    <td className="font-medium">{c.proveedor || <span className="text-gray-300">Sin proveedor</span>}</td>
                    <td className="text-gray-500">{c.num_items} ítem/s</td>
                    <td className="tabular-nums font-semibold text-gray-900">{moneda(c.total)}</td>
                    <td className="text-gray-400 text-xs">{c.usuario}</td>
                    <td>
                      <button
                        onClick={() => verDetalle(c.id)}
                        disabled={loadingDetalle}
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
                      >
                        <Eye size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal nueva compra */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Nueva compra"
        size="lg"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Registrando...' : 'Registrar compra'}
            </button>
          </>
        }
      >
        <div className="space-y-5">
          {/* Cabecera */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label>Fecha *</label>
              <input className="input" type="date" value={form.fecha}
                onChange={e => setForm(p => ({ ...p, fecha: e.target.value }))} />
            </div>
            <div>
              <label>Proveedor</label>
              <select className="input" value={form.proveedor_id}
                onChange={e => setForm(p => ({ ...p, proveedor_id: e.target.value }))}>
                <option value="">Sin proveedor</option>
                {proveedores.map(p => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label>Notas</label>
            <textarea className="input resize-none" rows={2} placeholder="Opcional..."
              value={form.notas} onChange={e => setForm(p => ({ ...p, notas: e.target.value }))} />
          </div>

          {/* Ítems */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="mb-0">Productos *</label>
              <button className="btn-ghost text-sm gap-1 text-primary-600 hover:text-primary-700 hover:bg-primary-50" onClick={addItem}>
                <Plus size={15} /> Agregar ítem
              </button>
            </div>

            {/* Header col */}
            <div className="grid grid-cols-12 gap-2 mb-1 px-1">
              <div className="col-span-5 text-xs text-gray-400 uppercase">Producto</div>
              <div className="col-span-2 text-xs text-gray-400 uppercase text-right">Cant.</div>
              <div className="col-span-3 text-xs text-gray-400 uppercase text-right">P. Unitario</div>
              <div className="col-span-1 text-xs text-gray-400 uppercase text-right">Total</div>
              <div className="col-span-1" />
            </div>

            <div className="space-y-2">
              {items.map((item, i) => (
                <ItemRow key={i} item={item} index={i} productos={productos}
                  onChange={updateItem} onRemove={removeItem} />
              ))}
            </div>

            {/* Total */}
            <div className="flex justify-end pt-4 border-t border-gray-100 mt-4">
              <div className="text-right">
                <span className="text-gray-400 text-sm mr-3">Total estimado</span>
                <span className="text-2xl font-bold text-gray-900">{moneda(total)}</span>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Detalle (read-only) */}
      <DetalleModal compra={detalle} onClose={() => setDetalle(null)} />
    </div>
  );
}
